/* eslint-disable react-refresh/only-export-components --
 * The provider and the consumer hooks are intrinsically paired and live in
 * one module by design (matches workshop/shell/PendingEdits.jsx). Splitting
 * them buys nothing structural and breaks the side-by-side port.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ReactNode } from "react";

// Global pending-edits store for inline editing. Each screen registers a
// 'scope' via usePendingScope:
//   - dirty: are there unsaved local edits?
//   - save():    persists (Promise) - called by the SaveBar
//   - discard(): drops local edits
//
// The SaveBar reads all scopes and offers a single Verwerfen/Speichern
// flow for the whole app. One consistent save shape across all pages.
//
// Ported node-by-node from workshop/shell/PendingEdits.jsx.

export interface ScopeState {
  dirty: boolean;
  awaitingConfirmation?: boolean;
  /**
   * Persist this scope's edits, or REJECT WITH A `LocalizedError` saying why not
   * (`@/lib/localized-error`).
   *
   * The unsaved-changes dialog shows that message, so it has to be product copy the reader can act
   * on — already translated, never genesis's own prose (@adr SPARK-0010-no-raw-server-errors-in-ui@1). Rejecting with any other error
   * is allowed and safe: the dialog falls back to a generic "Save failed." and logs the real one.
   * It is simply less useful, so a deliberate refusal should always be a LocalizedError.
   */
  save: () => void | Promise<void>;
  discard: () => void;
}

interface ContextValue {
  scopes: Record<string, ScopeState>;
  setScope: (id: string, patch: Partial<ScopeState>) => void;
  removeScope: (id: string) => void;
  /**
   * Monotonically-increasing counter, bumped by the SaveBar after every
   * successful "Speichern" cycle. Inline-edit primitives (EditableRow) watch
   * this tick to auto-collapse open editors once the user has saved.
   */
  saveTick: number;
  /** Bump `saveTick` after a successful save. Called by the SaveBar. */
  signalSaved: () => void;
}

const PendingEditsContext = createContext<ContextValue | null>(null);

export function PendingEditsProvider({ children }: { children: ReactNode }) {
  const [scopes, setScopes] = useState<Record<string, ScopeState>>({});
  const [saveTick, setSaveTick] = useState(0);

  const setScope = useCallback((id: string, patch: Partial<ScopeState>) => {
    setScopes((s) => ({
      ...s,
      [id]: { ...(s[id] ?? { dirty: false, save: noop, discard: noop }), ...patch },
    }));
  }, []);

  const removeScope = useCallback((id: string) => {
    setScopes((s) => {
      if (!(id in s)) return s;
      const next = { ...s };
      delete next[id];
      return next;
    });
  }, []);

  const signalSaved = useCallback(() => {
    setSaveTick((t) => t + 1);
  }, []);

  const value = useMemo<ContextValue>(
    () => ({ scopes, setScope, removeScope, saveTick, signalSaved }),
    [scopes, setScope, removeScope, saveTick, signalSaved],
  );

  return <PendingEditsContext.Provider value={value}>{children}</PendingEditsContext.Provider>;
}

export function usePendingEdits(): ContextValue | null {
  return useContext(PendingEditsContext);
}

interface PendingScopeHandlers {
  dirty: boolean;
  awaitingConfirmation?: boolean;
  save: () => void | Promise<void>;
  discard: () => void;
}

// Screen hook: registers dirty status + save/discard with the store.
// save/discard are kept current via a ref (no re-registration needed).
// Matches workshop/shell/PendingEdits.jsx node-by-node.
export function usePendingScope(id: string, handlers: PendingScopeHandlers): void {
  const ctx = useContext(PendingEditsContext);
  const ref = useRef(handlers);

  const setScope = ctx?.setScope;
  const removeScope = ctx?.removeScope;
  const { dirty, save, discard, awaitingConfirmation = false } = handlers;

  useEffect(() => {
    ref.current = { dirty, save, discard, awaitingConfirmation };
  }, [dirty, save, discard, awaitingConfirmation]);

  useEffect(() => {
    if (!setScope) return;
    setScope(id, {
      dirty,
      awaitingConfirmation,
      save: () => ref.current.save(),
      discard: () => ref.current.discard(),
    });
  }, [id, dirty, awaitingConfirmation, setScope]);

  useEffect(() => {
    if (!removeScope) return;
    return () => removeScope(id);
  }, [id, removeScope]);
}

function noop() {
  /* default scope handler */
}
